'use client';

import useAppStore from '@/core/stores/app';
import { faX } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";

interface PopupWrapperProps {
    close?: boolean;
    openByScroll?: boolean;
    openAfterSeconds?: number
    children: React.ReactNode;
    identifier: string; // Unique identifier for the popup
}

const PopupWrapper: React.FC<PopupWrapperProps> = ({ children, identifier, close = false, openAfterSeconds = 20, openByScroll = true }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isVisited, setIsVisited] = useState(false)

    const { popUpStatus, setPopUpStatus, togglePopUp } = useAppStore()

    useEffect(() => {
        if (close) {
            handleClose()
        }
    }, [close])

    const timer = useRef<NodeJS.Timeout>()


    useEffect(() => {
        const hasVisited = (Cookies.get(identifier) === "visited") || isVisited;
        const handleScroll = () => {
            const scrollPosition = window.scrollY + window.innerHeight;
            const halfPageHeight = document.body.scrollHeight / 2;

            if (scrollPosition >= halfPageHeight) {
                setIsVisible(true);
            }
        };

        if (hasVisited) {

            window.removeEventListener("scroll", handleScroll);
            clearTimeout(timer.current);
            return
        };

        timer.current = setTimeout(() => {
            setIsVisible(true);
        }, openAfterSeconds * 1000);


        if (openByScroll) {

            window.addEventListener("scroll", handleScroll);
        }

        return () => {
            clearTimeout(timer.current);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [identifier, isVisited]);

    const handleClose = () => {
        setIsVisible(false);
        togglePopUp(identifier)
        setIsVisited(true)
        Cookies.set(identifier, "visited", { expires: 14 });
    };


    useEffect(() => {
        if (isVisible && Object.values(popUpStatus).every((status) => !status)) {

            timer.current = setTimeout(() => {
                
                setPopUpStatus({ [identifier]: true });
            }, 3 * 1000);
        }
    }, [...Object.values(popUpStatus), isVisible]);

    return (
        <AnimatePresence>
            {popUpStatus[identifier] && (
                <>
                    {/* Blur Background */}
                    <motion.div

                        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[8000]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={handleClose} // Close pop-up when clicking the background
                    />
                    {/* Centered Pop-up */}
                    <motion.div
                        className="fixed top-1/2 left-1/2 transform overflow-hidden -translate-y-1/2 w-fit
                                    rounded-xl bg-white shadow-indigo-200 shadow-lg z-[8001]"
                        initial={{ scale: 0.8, opacity: 0, x: '-50%', y: '-50%' }}
                        animate={{ scale: 1, opacity: 1, x: '-50%', y: '-50%' }}
                        exit={{ scale: 0.8, opacity: 0, x: '-50%', y: '-50%' }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                        <div className="relative">
                            {/* Close Button */}
                            <button
                                onClick={handleClose}
                                aria-label='pop-up-button'
                                className="absolute top-0 right-0 p-2 rounded text-neutral-700 flex items-center justify-center text-xs font-bold"
                            >
                                <FontAwesomeIcon size="lg" icon={faX} />
                            </button>
                            {children}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default PopupWrapper;
